import { useState, useMemo, useCallback } from "react";
import type { Channel as StreamChannel } from "stream-chat";
import { useDisclosedNamesMapQuery } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import { useMutualFollowerIdsQuery } from "Api/Queries/Social/useMutualFollowerIdsQuery";
import type { ChatChannelFilters } from "Types/Chat/chatFilters";
import {
  type ChatChannelCategory,
  getChatChannelCategoriesForRole,
} from "Components/Chat/chatChannelCategories";
import { useCurrentUserAccessRole } from "Hooks/useCurrentUserAccessRole";
import { ChannelKind } from "Types/Chat/channelKind";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { getChannelDisplayName } from "Utils/Chat/getChannelDisplayName";
import { resolveMemberNames } from "Utils/Chat/resolveMemberNames";
import { Resources, useResource } from "Translations/Resources";
import { useDebounce } from "Hooks/useDebounce";
import {
  buildChannelSearchFilters,
  findDisclosedIdsMatchingTerm,
} from "Utils/Chat/buildChannelSearchFilters";

export const useChatChannelFilters = (userId: string) => {
  const { t } = useResource();
  const [searchText, setSearchText] = useState("");
  const [storedCategory, setSelectedCategory] =
    useState<ChatChannelCategory>("All");
  // A persona only ever holds a tab it has (chatChannelCategories): anything else — a jump
  // through ChatCategoryProvider, a persona switch — lands on All.
  const roleCode = useCurrentUserAccessRole();
  const selectedCategory: ChatChannelCategory = getChatChannelCategoriesForRole(
    roleCode,
  ).includes(storedCategory)
    ? storedCategory
    : "All";
  const [isShowingRejected, setIsShowingRejected] = useState(false);

  // A typed term searches ALL of the user's conversations, server-side, regardless of the
  // category tab — the tabs are hidden while it is present. `isSearching` follows the raw text
  // so the UI reacts on the first keystroke; the Stream query follows the debounced term.
  const trimmedSearchText = searchText.trim();
  const isSearching = trimmedSearchText.length > 0;
  const debouncedSearchTerm = useDebounce(trimmedSearchText, 300);
  // Debounce only on the way IN. Clearing the field (or switching tab, which clears it) must
  // return to the tab filter at once: otherwise the debounced term outlives the text for 300 ms,
  // the tabs reappear over the search results, and a second query follows.
  const searchTerm = isSearching ? debouncedSearchTerm : "";

  // The Mutual category is decided by the LOCAL follow graph, not by channel
  // data: unfollowing removes the channel from the tab without any Stream write. The filter has
  // no answer until the set arrives, so the Mutual tab reports itself as still pending rather
  // than rendering an empty list that reads as "you have no mutual-follower DMs".
  const { data: mutuals, isPending: isMutualIdsPending } =
    useMutualFollowerIdsQuery();
  // Names, on the other hand, come from the disclosed-names list (mutuals plus whoever messaged
  // this viewer first, App ADR-0011) — the Mutual tab must not widen to it.
  const { data: disclosedNames } = useDisclosedNamesMapQuery();

  const handleCategoryChange = useCallback((category: ChatChannelCategory) => {
    setSelectedCategory(category);
    setSearchText("");
    setIsShowingRejected(false);
  }, []);

  const filters: ChatChannelFilters = useMemo(() => {
    if (searchTerm) {
      return buildChannelSearchFilters({
        userId,
        term: searchTerm,
        matchingDisclosedIds: findDisclosedIdsMatchingTerm(
          disclosedNames,
          searchTerm,
        ),
      });
    }

    const base: ChatChannelFilters = {
      type: "messaging",
      members: { $in: [userId] },
    };

    if (selectedCategory === "Requests") {
      return {
        ...base,
        invite: isShowingRejected ? "rejected" : "pending",
      };
    }

    if (selectedCategory === "Groups") {
      return { ...base, channelKind: ChannelKind.Group };
    }

    if (selectedCategory === "Mutual" || selectedCategory === "Shopping") {
      return { ...base, channelKind: ChannelKind.Direct };
    }

    return base;
  }, [userId, selectedCategory, isShowingRejected, searchTerm, disclosedNames]);

  const channelRenderFilterFn = useCallback(
    (channels: Array<StreamChannel>) => {
      // Client-side invite filtering (safety net for real-time events). A search never shows
      // requests, whatever tab it was started from.
      let filteredChannels = channels.filter(({ state: { membership } }) => {
        const isPendingInvite =
          membership?.invited && !membership?.invite_accepted_at;
        const isRejectedInvite =
          membership?.invited && membership?.invite_rejected_at;

        if (selectedCategory === "Requests" && !isSearching) {
          if (isShowingRejected) {
            return isRejectedInvite;
          }
          return isPendingInvite;
        }

        // For all other views, exclude pending and rejected invites
        return !isPendingInvite && !isRejectedInvite;
      });

      if (
        !isSearching &&
        (selectedCategory === "Mutual" || selectedCategory === "Shopping")
      ) {
        filteredChannels = filteredChannels.filter(({ state: { members } }) => {
          const otherMember = Object.values(members).find(
            ({ user_id }) => user_id !== userId,
          );

          if (!otherMember) {
            return false;
          }

          if (selectedCategory === "Shopping") {
            return otherMember.user?.accessRole === AccessRoleCodes.Merchant;
          }

          // Mutual = DMs with mutual followers; the counterpart's Stream user id is their
          // identityUserPublicID (seam documented in chatConstants.ts). Follows are
          // Customer↔Customer, so this set can never overlap with Shopping's merchants.
          return (
            otherMember.user_id != null && !!mutuals?.has(otherMember.user_id)
          );
        });
      }

      // The server already narrowed the channels (buildChannelSearchFilters); this pass keeps
      // only those whose DISPLAYED name carries the term, so a match on a name the viewer is not
      // shown — a mutual follower's @handle under their real name — never surfaces an unexplained row.
      const sanitizedSearchText = trimmedSearchText.toLowerCase();
      if (sanitizedSearchText) {
        const anonymousName = t(Resources.Common.AnonymousUser);
        const unnamedChannelName = t(Resources.Common.UnnamedChannel);
        filteredChannels = filteredChannels.filter((channel) => {
          // Resolved, not raw: Stream carries @handle for a private persona, so searching the raw
          // names would not match a private mutual follower by the real name the list actually shows them
          // under (ADR-0007).
          const otherMembers = resolveMemberNames(
            Object.values(channel.state.members).filter(
              ({ user_id }) => user_id !== userId,
            ),
            disclosedNames,
            {
              // A frozen channel is anonymised, so it must stop matching the person's real name
              // here too — otherwise search re-identifies what the list deliberately hides.
              isAnonymized: channel.data?.frozen === true,
              anonymousName,
            },
          );
          return getChannelDisplayName({
            channelName: channel.data?.name,
            otherMembers,
            anonymousName,
            unnamedChannelName,
          })
            .toLowerCase()
            .includes(sanitizedSearchText);
        });
      }

      return filteredChannels;
    },
    [
      selectedCategory,
      isShowingRejected,
      userId,
      isSearching,
      trimmedSearchText,
      mutuals,
      disclosedNames,
      t,
    ],
  );

  return {
    selectedCategory,
    setSelectedCategory: handleCategoryChange,
    searchText,
    setSearchText,
    isSearching,
    filters,
    channelRenderFilterFn,
    // Only Mutual depends on data the filter still has to fetch; every other category can
    // answer from the channels alone. A search answers from the server, and re-queries when the
    // mutuals map arrives (it feeds the mutual-id clause), so it is never pending.
    isChannelFilterPending:
      selectedCategory === "Mutual" && !isSearching && isMutualIdsPending,
    isShowingRejected,
    setIsShowingRejected,
  };
};
